(ns shared.core.components.recharts.nested-pie-chart
  (:require [shared.core.style :as style]
            [shared.core.util :as util]
            [shared.core.reactive :as re]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.components.recharts.label :as label]
            [shared.core.components.recharts.legend :as legend]
            [shared.core.components.recharts.tooltip :as tooltip]))

(style/add-css [".recharts-pie-sector" {:cursor "pointer"}])

(defn calculate-outer-opacity
  "Returns an opacity between 0.3 and 0.8 for subcategories"
  [position total-in-category]
  (if (<= total-in-category 1)
    0.8
    (let [max-opacity 0.8
          min-opacity 0.3
          range (- max-opacity min-opacity)
          step (if (pos? (dec total-in-category))
                 (/ position (dec total-in-category))
                 0)]
      (- max-opacity (* step range)))))

(defn group-by-category
  "Groups items by a category key function and returns aggregated data.

   Parameters:
   - items: Collection of items to group
   - category-fn: Function to extract category from item
   - color-fn: Function to get color for a category"
  [items category-fn color-fn]
  (let [grouped (group-by category-fn items)
        aggregated (map (fn [[category group-items]]
                          {:name category
                           :value (reduce + (map :value group-items))
                           :fill (color-fn category)})
                        grouped)]
    (vec aggregated)))

(defn prepare-nested-data
  "Prepares data for nested pie chart from a hierarchical data structure.

   Parameters:
   - items: Collection of items with hierarchy (e.g., [{:category 'A' :subcategory 'A1' :value 10} ...])
   - category-key: Keyword for main category (e.g., :category)
   - subcategory-key: Keyword for subcategory (e.g., :subcategory)
   - value-key: Keyword for value (e.g., :value)
   - category-colors: Map of category name to color
   - subcategory-colors: Map of subcategory name to color

   Returns:
   - Map with :inner-data and :outer-data vectors"
  [items category-key subcategory-key value-key category-colors subcategory-colors]
  (let [;; Group by category for inner ring
        by-category (group-by category-key items)
        inner-data (mapv (fn [[cat cat-items]]
                           {:name cat
                            :value (reduce + (map value-key cat-items))
                            :fill (get category-colors cat "#cccccc")})
                         by-category)

        ;; Group by subcategory for outer ring
        by-subcategory (group-by subcategory-key items)
        outer-data (mapv (fn [[subcat subcat-items]]
                           (let [;; Get the parent category for this subcategory
                                 parent-cat (category-key (first subcat-items))]
                             {:name subcat
                              :value (reduce + (map value-key subcat-items))
                              :fill (get subcategory-colors subcat
                                         (get category-colors parent-cat "#cccccc"))
                              :category parent-cat
                              :opacity 0.8}))
                         by-subcategory)]
    {:inner-data inner-data
     :outer-data outer-data}))

;; View components

(defn tooltip
  [{:keys [mouse-position inner-ring-name inner-data outer-data hovered-outer-name show-double-click-message?]}]
  (when (or inner-ring-name hovered-outer-name)
    (let [{:keys [x y]} @mouse-position
          related-items (if hovered-outer-name
                          (filter #(= (:category %) inner-ring-name) outer-data)
                          inner-data)]
      (when (or (seq related-items) show-double-click-message?)
        (let [category-total (reduce (fn [sum {:keys [value]}]
                                       (+ sum (or value 0)))
                                     0
                                     related-items)
              rows (vec (for [item related-items]
                          (let [value (:value item)
                                percentage (recharts.util/calculate-percentage value category-total)
                                is-hovered (= (:name item) (or hovered-outer-name inner-ring-name))
                                fill (:fill item)]
                            {:key (str (:category item) "." (:name item))
                             :label (or (:label-override item)
                                        (some-> (:name item) (util/->capitalize-words)))
                             :value (.toLocaleString value)
                             :percent (str (.toFixed percentage 1) "%")
                             :color fill
                             :highlight-color (util/hex->rgba fill 0.12)
                             :highlight? is-hovered
                             :indicator {:shape :square
                                         :color fill}})))]
          [tooltip/tooltip-panel
           {:style-map (tooltip/floating-style x y {:tooltip-width 336})
            :numeric-align :right
            :column-template "minmax(160px,1fr) 68px"
            :show-double-click-message? show-double-click-message?
            :rows rows}])))))

(defn center-label
  "Center label showing total count or hovered item, sized to fit the inner ring's
   hole (see `label/fit-center-text` for `scale`/`hole-size`)."
  [total hovered-item-data category-totals {:keys [scale hole-size]}]
  (let [value-str (.toLocaleString (or (:value hovered-item-data) total 0))
        {:keys [value-font percentage-font name-font name-max-width name-max-height]}
        (label/fit-center-text {:scale scale
                                :hole-size hole-size
                                :value-str value-str})
        value-style {:font-size value-font
                     :margin-bottom 4
                     :font-weight style/font-bold-weight
                     :color (style/alc-color "Text/primary")
                     :letter-spacing "0.41px"
                     :line-height "100%"}
        name-style {:font-size name-font
                    :font-weight style/font-regular-weight
                    :text-transform :uppercase
                    :letter-spacing "0px"
                    :line-height 1.2
                    :max-width name-max-width
                    :max-height name-max-height
                    :overflow :hidden
                    :margin "0 auto"}]
    [:div {:style {:position :absolute
                   :top "50%"
                   :left "50%"
                   :transform "translate(-50%, -50%)"
                   :text-align :center
                   :pointer-events :none
                   :z-index 1
                   :font-family style/roboto-font-family
                   :color (style/alc-color "Text/primary")}}
     (if hovered-item-data
       (let [{:keys [value category name center-label percentage]} hovered-item-data
             percentage-base (if category
                               (let [category-total (get category-totals category total)]
                                 (if (pos? (or category-total 0))
                                   category-total
                                   total))
                               total)
             percentage (or percentage
                            (recharts.util/calculate-percentage (or value 0) percentage-base))]
         [:<>
          [:div {:style value-style}
           value-str]
          [:div {:style {:font-size percentage-font
                         :font-weight style/font-regular-weight
                         :color (style/alc-color "Text/secondary")
                         :margin-bottom 4}}
           (str "(" (.toFixed percentage 1) "%)")]
          [:div {:style (assoc name-style :color "#99A1AF")}
           (util/->capitalize-words (or center-label name))]])
       [:<>
        [:div {:style value-style}
         value-str]
        [:div {:style (assoc name-style :color (style/alc-color "Text/secondary"))}
         "TOTAL"]])]))

(defn nested-pie-chart
  "Renders a nested (two-level) pie chart with legend.

   Parameters:
   - :inner-data - Vector of maps with :name, :value, and :fill keys for inner ring
   - :outer-data - Vector of maps with :name, :value, and :fill keys for outer ring
   - :width - Chart width (default: 500)
   - :height - Chart height (default: 500)
   - :inner-radius-start - Inner ring start radius % (default: 32)
   - :inner-radius-end - Inner ring end radius % (default: 56)
   - :outer-radius-start - Outer ring start radius % (default: 56)
   - :outer-radius-end - Outer ring end radius % (default: 80)
   - :show-center-label? - Show total in center (default: true)
   - :show-legend? - Show legend (default: true)"
  [_]
  (let [outer-click-timer* (atom nil)
        inner-click-timer* (atom nil)
        hovered-slice (re/ratom nil)
        inner-ring-hover (re/ratom nil)
        outer-ring-hover (re/ratom nil)
        hovered-item (re/ratom nil)
        mouse-position (re/ratom {:x 0 :y 0})]
    (fn [{:keys [inner-data outer-data
                 width height
                 inner-radius-start inner-radius-end
                 outer-radius-start outer-radius-end
                 show-center-label?
                 show-legend?
                 legend-column-max-count
                 legend-source
                 total
                 tooltip-config]
          :or {width 500
               height 500
               inner-radius-start 32
               inner-radius-end 56
               outer-radius-start 58
               outer-radius-end 80
               show-center-label? true
               show-legend? true}}]
      (let [total (or total (recharts.util/calculate-total outer-data))
            legend-data (recharts.util/prepare-legend-data {:data (if (= legend-source :outer-data)
                                                                    outer-data
                                                                    inner-data)})
            category-totals (into {} (map (juxt :name :value) inner-data))
            current-hover @hovered-slice
            current-inner-hover @inner-ring-hover
            current-outer-hover @outer-ring-hover
            current-hovered-item @hovered-item
            has-hover (some? current-hover)
            has-inner-hover (some? current-inner-hover)
            has-outer-hover (some? current-outer-hover)
            ;; Get parent category when hovering outer ring - use the hovered item directly to avoid duplicates
            parent-category (when (and has-outer-hover current-hovered-item)
                              (:category current-hovered-item))
            ;; Get related outer ring items when hovering inner ring or outer ring
            ;; Use a set of unique keys (category.name) to avoid duplicates
            related-outer-keys (cond
                                 has-inner-hover
                                 (set (map #(str (:category %) "." (:name %)) (filter #(= (:category %) current-inner-hover) outer-data)))
                                 has-outer-hover
                                 (set (map #(str (:category %) "." (:name %)) (filter #(= (:category %) parent-category) outer-data)))
                                 :else nil)
            ;; Calculate opacity for hover effect
            calc-opacity (fn [item base-opacity is-outer is-inner]
                           (let [item-name (:name item)
                                 item-key (if is-outer (str (:category item) "." item-name) item-name)
                                 is-hovered-outer (and is-outer current-hovered-item
                                                       (= (:category item) (:category current-hovered-item))
                                                       (= (:name item) (:name current-hovered-item)))
                                 is-parent-inner (and is-inner current-hovered-item
                                                      (= item-name (:category current-hovered-item)))
                                 clamp (fn [val] (if is-outer (min 0.8 val) val))]
                             (cond
                               ;; Outer ring hover
                               has-outer-hover
                               (cond
                                 ;; Hovered outer ring slice
                                 is-hovered-outer
                                 (clamp 1.0)
                                 ;; Parent inner ring slice
                                 is-parent-inner
                                 (clamp 1.0)
                                 ;; All other slices (inner and outer) should be 33%
                                 :else
                                 (clamp 0.33))
                               ;; Inner ring hover
                               has-inner-hover
                               (cond
                                 ;; Hovered inner ring slice
                                 (and is-inner (= current-inner-hover item-name))
                                 1.0
                                 ;; Other inner ring slices
                                 is-inner
                                 0.33
                                 ;; Related outer ring slices (use unique key)
                                 (and is-outer (contains? related-outer-keys item-key))
                                 (clamp base-opacity)
                                 ;; Unrelated outer ring slices
                                 is-outer
                                 (clamp 0.33))
                               ;; Regular hover (outer ring only, no tooltip)
                               has-hover
                               (if (= current-hover item-name)
                                 (clamp base-opacity)
                                 (clamp (* base-opacity 0.6)))
                               ;; No hover
                               :else (clamp base-opacity))))
            ;; Add opacity to data based on hover state
            inner-data-with-opacity (mapv #(assoc % :opacity (calc-opacity % 1.0 false true)) inner-data)
            outer-data-with-opacity (mapv #(assoc % :opacity (calc-opacity % (or (:opacity %) 0.8) true false)) outer-data)
            max-size (min width height)]
        [legend/chart-with-legend
         {:legend-data legend-data
          :show-legend? show-legend?
          :column-max-count legend-column-max-count
          :max-size max-size
          :legend-gap 48}
         (fn [size]
           [:div {:style {:width (str size "px")
                          :height (str size "px")
                          :max-width "100%"
                          :aspect-ratio 1
                          :font-family style/roboto-font-family
                          :position :relative
                          :flex-shrink 0}
                  :on-mouse-move (fn [e]
                                   (reset! mouse-position {:x (.-clientX e)
                                                           :y (.-clientY e)}))}
            [recharts.util/re-responsive-container {:width "100%" :height size}
             [recharts.util/re-pie-chart {}
              [recharts.util/re-pie {:data (clj->js inner-data-with-opacity)
                                     :dataKey "value"
                                     :cx "50%"
                                     :cy "50%"
                                     :innerRadius (str inner-radius-start "%")
                                     :outerRadius (str inner-radius-end "%")
                                     :label #(label/text-label % {})
                                     :labelLine false
                                     :isAnimationActive false}
               ;; Apply individual opacity to inner ring cells
               (for [item inner-data-with-opacity]
                 (let [{:keys [on-click on-double-click]} item]
                   [recharts.util/re-cell {:key (:name item)
                                           :fill (:fill item)
                                           :fillOpacity (:opacity item)
                                           :stroke (style/alc-color "Background/card_hex")
                                           :style {:outline "none"}
                                           :onMouseEnter #(do (reset! hovered-slice (:name item))
                                                              (reset! inner-ring-hover (:name item))
                                                              (reset! outer-ring-hover nil)
                                                              (reset! hovered-item item))
                                           :onMouseLeave #(do (reset! hovered-slice nil)
                                                              (reset! inner-ring-hover nil)
                                                              (reset! hovered-item nil))

                                           :onClick (if (and on-click on-double-click)
                                                      #(do
                                                         (when @inner-click-timer* (js/clearTimeout @inner-click-timer*))
                                                         (reset! inner-click-timer* (js/setTimeout (fn [] (on-click % item))
                                                                                                   250)))
                                                      on-click)
                                           :onDoubleClick #(when on-double-click
                                                             (when @inner-click-timer*
                                                               (js/clearTimeout @inner-click-timer*)
                                                               (reset! inner-click-timer* nil))
                                                             (on-double-click % item)
                                                             (util/stop-event %))}]))]
              [recharts.util/re-pie {:data (clj->js outer-data-with-opacity)
                                     :dataKey "value"
                                     :cx "50%"
                                     :cy "50%"
                                     :innerRadius (str outer-radius-start "%")
                                     :outerRadius (str outer-radius-end "%")
                                     :label #(label/text-label % {})
                                     :labelLine false
                                     :isAnimationActive false}
               ;; Apply individual opacity to outer ring cells
               (for [item outer-data-with-opacity]
                 (let [display-opacity (:opacity item)
                       base-color (:fill item)
                       on-click (:on-click item)
                       on-double-click (:on-double-click item)
                       fill-color (util/hex->rgba base-color display-opacity)]
                   [recharts.util/re-cell {:key (str (:category item) "." (:name item))
                                           :fill fill-color
                                           :fillOpacity 1
                                           :stroke (style/alc-color "Background/card_hex")
                                           :style {:outline "none"}
                                           :onMouseEnter #(do (reset! hovered-slice (:name item))
                                                              (reset! inner-ring-hover (:category item))
                                                              (reset! outer-ring-hover (:name item))
                                                              (reset! hovered-item item))
                                           :onMouseLeave #(do (reset! hovered-slice nil)
                                                              (reset! inner-ring-hover nil)
                                                              (reset! outer-ring-hover nil)
                                                              (reset! hovered-item nil))
                                           :onClick (if (and on-click on-double-click)
                                                      #(do
                                                         (when @outer-click-timer* (js/clearTimeout @outer-click-timer*))
                                                         (reset! outer-click-timer* (js/setTimeout (fn [] (on-click % item))
                                                                                                   250)))
                                                      on-click)
                                           :onDoubleClick #(when on-double-click
                                                             (when @outer-click-timer*
                                                               (js/clearTimeout @outer-click-timer*)
                                                               (reset! outer-click-timer* nil))
                                                             (on-double-click % item)
                                                             (util/stop-event %))}]))]]]
            (when show-center-label?
              [center-label total @hovered-item category-totals
               {:scale (/ size max-size)
                :hole-size (* size (/ inner-radius-start 100))}])
            [tooltip (merge {:mouse-position mouse-position
                             :inner-ring-name (or current-inner-hover parent-category)
                             :inner-data inner-data
                             :outer-data outer-data
                             :overall-total total
                             :hovered-outer-name current-outer-hover}
                            tooltip-config)]])]))))